import React, {useLayoutEffect} from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';

const LoginPage: React.FC = () => {
  const primaryColor = '#4277fc';
  const backgroundColor = primaryColor;
  useLayoutEffect(() => {
     document.body.style.backgroundColor = backgroundColor;
  });
  const [identifier, setIdentifier] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const [submitting, setSubmitting] = React.useState(false);
  const [resetDialogOpen, setResetDialogOpen] = React.useState(false);
  const [resetEmail, setResetEmail] = React.useState('');
  const [resetSubmitting, setResetSubmitting] = React.useState(false);
  const [resetError, setResetError] = React.useState<string | null>(null);
  const [resetSubmitted, setResetSubmitted] = React.useState(false);
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  if (isAuthenticated) return <Navigate to="/dashboard" replace />;

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const response = await fetch('/api/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ login: identifier, password }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Anmeldung fehlgeschlagen.');
      login(data.token);
      navigate('/dashboard', { replace: true });
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Verbindung zum Server fehlgeschlagen.');
    } finally {
      setSubmitting(false);
    }
  };

  const openResetDialog = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    setResetEmail(identifier.includes('@') ? identifier : '');
    setResetError(null);
    setResetSubmitted(false);
    setResetDialogOpen(true);
  };

  const closeResetDialog = () => {
    if (resetSubmitting) return;
    setResetDialogOpen(false);
    setResetError(null);
    setResetSubmitted(false);
  };

  const handleResetRequest = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setResetError(null);
    setResetSubmitting(true);
    try {
      const response = await fetch('/api/password-reset/request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: resetEmail.trim() }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Die Anfrage konnte nicht verarbeitet werden.');
      setResetSubmitted(true);
    } catch (requestError) {
      setResetError(requestError instanceof Error ? requestError.message : 'Verbindung zum Server fehlgeschlagen.');
    } finally {
      setResetSubmitting(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', bgcolor: backgroundColor, p: 2 }}>
      <Container component="main" maxWidth="xs">
        <Paper elevation={4} sx={{ p: { xs: 3, sm: 5 }, borderRadius: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Avatar sx={{ m: 1, bgcolor: 'primary.main' }}><LockOutlinedIcon /></Avatar>
            <Typography component="h1" variant="h5" sx={{ mb: 1 }}>Anmelden caix Kundenportal</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Melden Sie sich mit Ihrem Zugang an.</Typography>
            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1, width: '100%' }}>
              {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
              <TextField
                margin="normal"
                required
                fullWidth
                autoFocus
                label="Benutzername oder E-Mail-Adresse"
                autoComplete="username"
                value={identifier}
                onChange={(event) => setIdentifier(event.target.value)}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                label="Passwort"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
              <Button type="submit" fullWidth variant="contained" disabled={submitting} sx={{ mt: 3, mb: 2, py: 1.2 }}>
                {submitting ? 'Anmeldung läuft …' : 'Anmelden'}
              </Button>
            </Box>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ mt: 1 }}>
              <Link href="#passwort-vergessen" onClick={openResetDialog} underline="hover">
                Passwort vergessen
              </Link>
              <Typography variant="body2" color="text.secondary" aria-hidden="true">|</Typography>
              <Link href="https://www.caix.de/kontakt/" target="_blank" rel="noopener noreferrer" underline="hover">
                Impressum
              </Link>
            </Stack>
          </Box>
        </Paper>
      </Container>
      <Dialog open={resetDialogOpen} onClose={closeResetDialog} fullWidth maxWidth="xs" aria-labelledby="password-reset-title">
        <Box component="form" onSubmit={handleResetRequest}>
          <DialogTitle id="password-reset-title">Passwort zurücksetzen</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Geben Sie Ihre E-Mail-Adresse ein und wir senden Ihnen Anweisungen zum Zurücksetzen Ihres Passworts.
            </Typography>
            {resetError && <Alert severity="error" sx={{ mb: 2 }}>{resetError}</Alert>}
            {resetSubmitted ? (
              <Alert severity="success">
                Wenn ein Kundenkonto mit dieser E-Mail-Adresse besteht, erhalten Sie Anweisungen zum Zurücksetzen des Passworts.
              </Alert>
            ) : (
              <TextField
                autoFocus
                required
                fullWidth
                type="email"
                label="E-Mail-Adresse"
                autoComplete="email"
                value={resetEmail}
                onChange={(event) => setResetEmail(event.target.value)}
                disabled={resetSubmitting}
              />
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={closeResetDialog} disabled={resetSubmitting}>
              {resetSubmitted ? 'Schließen' : 'Abbrechen'}
            </Button>
            {!resetSubmitted && (
              <Button type="submit" variant="contained" disabled={resetSubmitting}>
                {resetSubmitting ? 'Wird gesendet …' : 'Anweisungen senden'}
              </Button>
            )}
          </DialogActions>
        </Box>
      </Dialog>
    </Box>
  );
};

export default LoginPage;
